import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import type { ReactNode } from 'react';
import type { VpnGatewayRecord, VpnPeerStatus } from '@fleet-cert-console/shared';
import { CopyRow } from '../lib/clipboard.js';
import { autostartLabel, handshakeAgoLabel, linkIndicator, yesNo } from '../lib/vpnLink.js';
import { formatBytes, timeAgo, vpnOnlineIndicator } from '../lib/vpnPeers.js';
import { vpnStatusIndicator } from '../lib/vpnStatus.js';
import { customerProjectLabel } from '../lib/vpnProvisioning.js';

export interface VpnGatewayDetailProps {
  gateway: VpnGatewayRecord;
  /** Dev hub EC2 instance id (from config); enables the copy-port-forward command when set. */
  hubInstanceId?: string;
  /** This gateway's live hub peer state (matched by gatewayPubKey), when the page has fetched it. */
  peer?: VpnPeerStatus;
  /** When the hub was read (ISO) — required to render the connectivity section. */
  peerFetchedAt?: string;
}

/** Direct SSH command over the VPN (gateway has a route to the hub / caller has a route to the VPN). */
export function sshCommand(vpnIp: string): string {
  return `ssh admin@${vpnIp}`;
}

/**
 * SSM port-forward tunnel through the hub, for callers with no direct route into the VPN CIDR.
 * Uses AWS CLI shorthand (not JSON) for --parameters: Windows PowerShell 5.1 strips embedded
 * double quotes from native-command args, mangling a JSON payload into a parse error — the
 * shorthand form survives PowerShell and bash alike (verified live 2026-09-11).
 */
export function portForwardCommand(hubInstanceId: string, vpnIp: string): string {
  return `aws ssm start-session --target ${hubInstanceId} --document-name AWS-StartPortForwardingSessionToRemoteHost --parameters "host=${vpnIp},portNumber=22,localPortNumber=2222" --profile vxdev --region us-east-1`;
}

/** Port-forward for the gateway's web interface: browse http://127.0.0.1:8080 while it runs. */
export function webUiPortForwardCommand(hubInstanceId: string, vpnIp: string): string {
  return `aws ssm start-session --target ${hubInstanceId} --document-name AWS-StartPortForwardingSessionToRemoteHost --parameters "host=${vpnIp},portNumber=80,localPortNumber=8080" --profile vxdev --region us-east-1`;
}

const truncateMiddle = (k: string) => (k.length > 20 ? `${k.slice(0, 10)}…${k.slice(-8)}` : k);

function TimelineStep({
  label,
  reached,
  timestamp,
  detail,
}: {
  label: string;
  reached: boolean;
  timestamp?: string;
  detail?: ReactNode;
}) {
  const hasSubtext = timestamp !== undefined || detail !== undefined;
  return (
    <Box>
      <StatusIndicator type={reached ? 'success' : 'stopped'}>{label}</StatusIndicator>
      {hasSubtext && (
        <Box fontSize="body-s" color="text-body-secondary" padding={{ left: 'l' }}>
          {timestamp}
          {timestamp && detail ? ' — ' : ''}
          {detail}
        </Box>
      )}
    </Box>
  );
}

/**
 * Gateway detail split panel: key facts, an enrollment timeline built only from persisted fields
 * (never fabricated — a step whose timestamp isn't recorded renders without one), and copyable
 * connect commands. Rendered fresh on every poll tick since `gateway` is the live table row.
 */
export function VpnGatewayDetail({ gateway: g, hubInstanceId, peer, peerFetchedAt }: VpnGatewayDetailProps) {
  const status = vpnStatusIndicator(g.vpnStatus);

  // Key reported: while the row is still sitting in `requested`, updatedAt IS the key-report time.
  // Once the status moves on, updatedAt reflects a later event, so show a checkmark with no time
  // rather than a stale/misleading timestamp.
  const keyReported = Boolean(g.gatewayPubKey);
  const keyReportedTimestamp = keyReported && g.vpnStatus === 'requested' ? g.updatedAt : undefined;

  // Hub provisioned: the allocator only ever writes vpnProvisionCommandId AFTER a successful SSM
  // SendCommand call (per the control plane's design — status flips to `provisioning` first, the
  // commandId lands after). So its presence is the persisted fact that hub provisioning actually ran,
  // regardless of whether the row later moved to applying/applied or died with vpnStatus `failed` —
  // deriving "reached" from status order alone would show "not reached" next to a real command id for
  // a gateway that failed after provisioning started.
  const hubProvisioned = Boolean(g.vpnProvisionCommandId);
  const applied = g.vpnStatus === 'applied';

  // Gateway-reported link state (spec 7.3). Absent on rows that never answered a link command.
  const link = linkIndicator(g);
  const hasLinkReport = link !== null || g.vpnLinkUpdatedAt !== undefined || g.vpnAutostart !== undefined || g.vpnLinkLastActionAt !== undefined;
  const hasVersions =
    g.vpnClientVersion != null || g.gatewayFirmwareVersion != null || g.forwarderVersion != null || g.gatewayVendor != null;

  // Null for a gateway with no key (not enrolled): the hub cannot know it, so no online/offline verdict.
  const online = peerFetchedAt ? vpnOnlineIndicator(g.gatewayPubKey, peer, peerFetchedAt) : null;

  return (
    <SpaceBetween size="l">
      {g.provisioning && (
        <Box>
          <Box variant="h4" padding={{ bottom: 'xs' }}>
            Provisioning
          </Box>
          <ColumnLayout columns={3} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Status</Box>
              <StatusIndicator type={g.provisioning.status === 'COMPLETE' ? 'success' : 'pending'}>
                {g.provisioning.status.toLowerCase()}
              </StatusIndicator>
            </div>
            <div>
              <Box variant="awsui-key-label">Confirmed</Box>
              <Box>{g.provisioning.confirmedAt ?? '—'}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Customer / project</Box>
              <Box>{customerProjectLabel(g.provisioning)}</Box>
            </div>
          </ColumnLayout>
        </Box>
      )}
      <ColumnLayout columns={4} variant="text-grid">
        <div>
          <Box variant="awsui-key-label">Status</Box>
          <StatusIndicator type={status.type}>{status.label}</StatusIndicator>
          {g.vpnLastResponseAt && (
            <Box fontSize="body-s" color="text-body-secondary">
              Last response {g.vpnLastResponseAt}
            </Box>
          )}
        </div>
        <div>
          <Box variant="awsui-key-label">VPN address / DNS</Box>
          {g.vpnIp ? <CopyRow value={g.vpnIp} label="VPN address" /> : <Box>—</Box>}
          {g.vpnDnsName && (
            <Box fontSize="body-s" color="text-body-secondary">
              <CopyRow value={g.vpnDnsName} label="DNS name" />
            </Box>
          )}
        </div>
        <div>
          <Box variant="awsui-key-label">WireGuard key</Box>
          {g.gatewayPubKey ? (
            <CopyRow value={g.gatewayPubKey} label="WireGuard key" display={truncateMiddle(g.gatewayPubKey)} />
          ) : (
            <Box>—</Box>
          )}
        </div>
        <div>
          <Box variant="awsui-key-label">Approved by</Box>
          <Box>{g.approvedBy ?? '—'}</Box>
          {g.approvalReason && (
            <Box fontSize="body-s" color="text-body-secondary">
              {g.approvalReason}
            </Box>
          )}
        </div>
      </ColumnLayout>

      <div>
        <Box variant="awsui-key-label" padding={{ bottom: 'xs' }}>
          Enrollment timeline
        </Box>
        <SpaceBetween size="s">
          <TimelineStep label="Initiated" reached={Boolean(g.vpnRequestPublishedAt)} timestamp={g.vpnRequestPublishedAt} />
          <TimelineStep label="Key reported" reached={keyReported} timestamp={keyReportedTimestamp} />
          <TimelineStep
            label="Approved"
            reached={Boolean(g.vpnApprovedAt)}
            timestamp={g.vpnApprovedAt}
            detail={g.approvedBy}
          />
          <TimelineStep
            label="Hub provisioned"
            reached={hubProvisioned}
            detail={g.vpnProvisionCommandId ? truncateMiddle(g.vpnProvisionCommandId) : undefined}
          />
          {/* vpnLastResponseAt moves on every accepted result (link results too); vpnAppliedAt is the stable apply time.
              Legacy rows applied before vpnAppliedAt existed fall back to vpnLastResponseAt only while no link action has
              been forwarded; after that it may be a link-response time, so show none rather than a fabricated apply time. */}
          <TimelineStep label="Applied" reached={applied} timestamp={applied ? (g.vpnAppliedAt ?? (g.vpnLinkLastActionAt ? undefined : g.vpnLastResponseAt)) : undefined} />
        </SpaceBetween>
      </div>

      {peerFetchedAt && (
        <div>
          <Box variant="awsui-key-label" padding={{ bottom: 'xs' }}>
            Connectivity (live from the hub)
          </Box>
          <ColumnLayout columns={4} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">VPN online</Box>
              {online ? <StatusIndicator type={online.type}>{online.label}</StatusIndicator> : <Box>—</Box>}
            </div>
            <div>
              <Box variant="awsui-key-label">Last seen</Box>
              <Box>{peer?.latestHandshakeAt ? timeAgo(peer.latestHandshakeAt) : peer ? 'never' : '—'}</Box>
              {peer?.latestHandshakeAt && (
                <Box fontSize="body-s" color="text-body-secondary">
                  {peer.latestHandshakeAt}
                </Box>
              )}
            </div>
            <div>
              <Box variant="awsui-key-label">Transfer (rx / tx)</Box>
              <Box>{peer ? `${formatBytes(peer.rxBytes)} / ${formatBytes(peer.txBytes)}` : '—'}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Peer endpoint</Box>
              {peer?.endpoint ? <CopyRow value={peer.endpoint} label="peer endpoint" /> : <Box>—</Box>}
            </div>
          </ColumnLayout>
          {!g.gatewayPubKey && (
            <Box fontSize="body-s" color="text-body-secondary" padding={{ top: 'xxs' }}>
              Not enrolled: the gateway has not reported a WireGuard key, so there is no peer to look for on the hub.
            </Box>
          )}
          {!peer && g.gatewayPubKey && (
            <Box fontSize="body-s" color="text-body-secondary" padding={{ top: 'xxs' }}>
              No peer with this gateway&apos;s key on the hub — not provisioned there yet, or the key rotated.
            </Box>
          )}
        </div>
      )}

      <div>
        <Box variant="awsui-key-label" padding={{ bottom: 'xs' }}>
          Link (as reported by the gateway)
        </Box>
        {!hasLinkReport ? (
          <Box fontSize="body-s" color="text-body-secondary">
            Not reported yet — the gateway has not answered a link command (needs client 2.2.0 or later).
          </Box>
        ) : (
          <ColumnLayout columns={4} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Link</Box>
              {link ? <StatusIndicator type={link.type}>{link.label}</StatusIndicator> : <Box>—</Box>}
              <Box fontSize="body-s" color="text-body-secondary">
                state {g.vpnLinkState ?? '—'} · hold {yesNo(g.vpnHeld, 'set', 'clear')}
              </Box>
              {g.vpnLinkUpdatedAt && (
                <Box fontSize="body-s" color="text-body-secondary">
                  Reported {g.vpnLinkUpdatedAt}
                </Box>
              )}
            </div>
            <div>
              <Box variant="awsui-key-label">Handshake (gateway side)</Box>
              <Box>{yesNo(g.vpnLinkConnected, 'connected', 'not connected')}</Box>
              <Box fontSize="body-s" color="text-body-secondary">
                {handshakeAgoLabel(g.vpnLinkHandshakeAgoSec)}
              </Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Autostart</Box>
              <Box>{autostartLabel(g.vpnAutostart)}</Box>
              <Box fontSize="body-s" color="text-body-secondary">
                supervisor {yesNo(g.vpnSupervisorRunning, 'running', 'not running')}
              </Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Last link action</Box>
              <Box>{g.vpnLinkLastAction ?? '—'}</Box>
              {g.vpnLinkLastActionAt && (
                <Box fontSize="body-s" color="text-body-secondary">
                  {g.vpnLinkLastActionAt}
                  {g.vpnLinkLastExecId !== undefined ? ` — exec ${g.vpnLinkLastExecId}` : ''}
                </Box>
              )}
              {typeof g.vpnLinkLastExecId === 'number' && (
                <Box fontSize="body-s" color="text-body-secondary">
                  Answered: {yesNo(g.vpnLastExecId === g.vpnLinkLastExecId, 'yes', 'no')}
                </Box>
              )}
            </div>
          </ColumnLayout>
        )}
        {hasLinkReport && g.vpnLinkUpdatedAt === undefined && (
          <Box fontSize="body-s" color="text-body-secondary" padding={{ top: 'xs' }}>
            No answer yet — a link command was sent but the gateway has not reported (needs client 2.2.0 or later).
          </Box>
        )}
      </div>

      {hasVersions && (
        <div>
          <Box variant="awsui-key-label" padding={{ bottom: 'xs' }}>
            Versions
          </Box>
          <ColumnLayout columns={4} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Client</Box>
              <Box>{g.vpnClientVersion ?? '—'}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Firmware</Box>
              <Box>{g.gatewayFirmwareVersion ?? '—'}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Forwarder</Box>
              <Box>{g.forwarderVersion ?? '—'}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Vendor</Box>
              <Box>{g.gatewayVendor ?? '—'}</Box>
            </div>
          </ColumnLayout>
        </div>
      )}

      <div>
        <Box variant="awsui-key-label" padding={{ bottom: 'xs' }}>
          Connect
        </Box>
        <SpaceBetween size="xs">
          {g.vpnIp ? (
            <CopyRow value={sshCommand(g.vpnIp)} label="SSH command" />
          ) : (
            <Box fontSize="body-s" color="text-body-secondary">
              No VPN address yet.
            </Box>
          )}
          {g.vpnIp && hubInstanceId && (
            <>
              <CopyRow value={portForwardCommand(hubInstanceId, g.vpnIp)} label="port-forward command" />
              <Box fontSize="body-s" color="text-body-secondary">
                Then <code>ssh -p 2222 admin@127.0.0.1</code>
              </Box>
            </>
          )}
        </SpaceBetween>
      </div>
    </SpaceBetween>
  );
}
